import { cn } from '@/lib/utils'
import { useDebounceFn } from 'ahooks'
import React from 'react'
import { SearchIcon, ZyguanbiTianchongIcon } from './icon'

type InputContextOption = {
  prefix?: React.ReactNode
  suffix?: React.ReactNode
  showClear?: boolean
  onClear?: (e: React.MouseEvent) => void
}

const InputContext = React.createContext<InputContextOption>({})

function Prefix({ children, className }: { children: React.ReactNode; className?: string }) {
  if (!children) return null

  return <div className={cn('order-1 mr-1 flex items-center  whitespace-nowrap', className)}>{children}</div>
}

function Suffix({ children, className }: { children: React.ReactNode; className?: string }) {
  if (!children) return null

  return <div className={cn('order-3 ml-2 flex items-center gap-2 whitespace-nowrap', className)}>{children}</div>
}

// 定义 Input 组件的类型
interface InputComponent
  extends React.ForwardRefExoticComponent<
    Omit<React.ComponentProps<'input'>, 'prefix' | 'suffix'> & {
      onValueChange?: (value: string) => void
      allowClear?: boolean
      wrapperClassName?: string
    } & React.RefAttributes<HTMLInputElement>
  > {
  Prefix: typeof Prefix
  Suffix: typeof Suffix
}

const Input = React.forwardRef<
  HTMLInputElement,
  Omit<React.ComponentProps<'input'>, 'prefix' | 'suffix'> & {
    onValueChange?: (value: string) => void
    allowClear?: boolean
    wrapperClassName?: string
  }
>(({ className, type, onChange, onValueChange, allowClear, value = '', wrapperClassName, children, ...props }, ref) => {
  const showClear = !!(allowClear && value)
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange?.({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>)
    onValueChange?.('')
  }
  const handleInputChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = ev.target.value
    onChange?.(ev)
    onValueChange?.(inputValue)
  }
  const handleInputBlur = (ev: React.FocusEvent<HTMLInputElement>) => {
    if (type === 'number') {
      const inputValue = ev.target.value
      const min = props.min !== undefined ? Number(props.min) : undefined
      const max = props.max !== undefined ? Number(props.max) : undefined
      if (inputValue !== '' && !isNaN(Number(inputValue))) {
        let numValue = Number(inputValue)
        if (min !== undefined && numValue < min) numValue = min
        if (max !== undefined && numValue > max) numValue = max
        if (String(numValue) !== inputValue) {
          // 只有需要修正时才触发
          onValueChange?.(String(numValue))
        }
      }
    }
    props.onBlur?.(ev)
  }
  return (
    <InputContext.Provider value={{ showClear, onClear: handleClear }}>
      <div
        className={cn(
          'px-2 w-fit relative flex items-center min-h-30px border group',
          'border border-input rounded-md bg-background',
          'hover:border-ring',
          'has-[:focus-visible]:border-ring has-[:focus-visible]:ring-ring/30 has-[:focus-visible]:ring-[2px]',
          '[&:has(input:disabled)]:bg-hover [&:has(input:disabled)]:border-active [&:has(input:disabled)]:hover:border-active',
          wrapperClassName,
        )}
      >
        <input
          ref={ref}
          type={type}
          value={value}
          data-slot="input"
          className={cn(
            'order-2 border-none file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground flex h-full min-h-7.5 w-full min-w-0 bg-transparent py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
            'aria-invalid:ring-danger/20 dark:aria-invalid:ring-danger/40 aria-invalid:border-danger',
            className,
          )}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          {...props}
        />
        {children}
        {showClear && (
          <div className="order-4  flex items-center">
            <button
              type="button"
              onClick={handleClear}
              className="text-muted-foreground hover:text-muted-foreground-2 hover:bg-hover p-1 rounded-sm active:text-muted-foreground-1 active:bg-active"
            >
              <ZyguanbiTianchongIcon className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </InputContext.Provider>
  )
}) as InputComponent

function InputWithCounter({
  className,
  type,
  onChange,
  onValueChange,
  allowClear,
  value = '',
  wrapperClassName,
  children,
  ...props
}: Omit<React.ComponentProps<'input'>, 'prefix' | 'suffix'> & {
  onValueChange?: (value: string) => void
  allowClear?: boolean
  wrapperClassName?: string
}) {
  const showClear = !!(allowClear && value)
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange?.({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>)
    onValueChange?.('')
  }

  return (
    <InputContext.Provider value={{ showClear, onClear: handleClear }}>
      <div
        className={cn(
          'px-2 w-fit relative flex items-center min-h-30px border group',
          'border border-input rounded-md bg-background',
          'hover:border-ring',
          'has-[:focus-visible]:border-ring has-[:focus-visible]:ring-ring/30 has-[:focus-visible]:ring-[2px]',
          wrapperClassName,
        )}
      >
        <input
          type={type}
          value={value}
          data-slot="input"
          className={cn(
            'order-2 border-none file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground flex h-full min-h-7.5 w-full min-w-0 bg-transparent py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
            'aria-invalid:ring-danger/20 dark:aria-invalid:ring-danger/40 aria-invalid:border-danger',
            className,
          )}
          onChange={(ev) => {
            onChange?.(ev)
            onValueChange?.(ev.target.value)
          }}
          {...props}
        />
        {children}
        {props.maxLength && (
          <Suffix>
            <span className="text-muted-foreground">{`${String(value || '').length || 0}/${props.maxLength}`}</span>
          </Suffix>
        )}
        {showClear && (
          <div className="order-4  flex items-center">
            <button
              type="button"
              onClick={handleClear}
              className="text-muted-foreground hover:text-muted-foreground-1 hover:bg-hover p-1 rounded-sm"
            >
              <ZyguanbiTianchongIcon className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </InputContext.Provider>
  )
}

function DebounceInput({
  onValueChange,
  value,
  debounceWait = 300,
  showSearch = true,
  wrapperClassName,
  ...props
}: React.ComponentProps<typeof Input> & {
  debounceWait?: number
  showSearch?: boolean
}) {
  const [inputValue, setInputValue] = React.useState(value)
  const [isComposing, setIsComposing] = React.useState(false)

  const { run } = useDebounceFn(
    (value: string) => {
      onValueChange?.(value)
    },
    { wait: debounceWait },
  )

  React.useEffect(() => {
    setInputValue(value)
  }, [value])

  return (
    <Input
      {...props}
      wrapperClassName={cn('w-60', wrapperClassName)}
      allowClear
      value={inputValue}
      onChange={(e) => {
        const newValue = e.target.value
        setInputValue(newValue)
        if (!isComposing) {
          run(newValue)
        }
      }}
      onCompositionStart={() => setIsComposing(true)}
      onCompositionEnd={(e) => {
        setIsComposing(false)
        const target = e.target as HTMLInputElement
        run(target.value)
      }}
    >
      {showSearch && (
        <Input.Prefix>
          <SearchIcon />
        </Input.Prefix>
      )}
    </Input>
  )
}

Input.Prefix = Prefix
Input.Suffix = Suffix
export { Input, DebounceInput, InputWithCounter }
